import { BillOfMaterialsLineItemType } from '../enums/bill-of-materials-line-item-type.enum';
import { HardwareItemUnit } from '../enums/hardware-item-unit.enum';
import { BillOfMaterialsLineItem } from '../models/bill-of-materials-line-item.model';
import { PlasticsHardwareItem } from '../models/plastics-hardware-item.model';
import { PlasticsMaterial } from '../models/plastics-material.model';
import { ProductVersion } from '../models/product-version.model';

export type PlasticsProductSurchargeIssueCode =
    | 'bom-load-failed'
    | 'circular-bom'
    | 'hardware-load-failed'
    | 'invalid-quantity'
    | 'invalid-surcharge-history'
    | 'material-load-failed'
    | 'material-recipe-unresolved'
    | 'product-version-load-failed'
    | 'unsupported-weight-unit';

export interface PlasticsProductSurchargeIssue {
    code: PlasticsProductSurchargeIssueCode;
    entityId: string;
    message: string;
}

export interface PlasticsProductSurchargeContribution {
    hardwareItemId: string;
    partNumber: string;
    description: string;
    quantityPounds: number;
    surchargeCentsPerPound: number;
    surchargeDollars: number;
}

export interface PlasticsProductSurchargeResult {
    totalSurchargeDollars: number;
    complete: boolean;
    contributions: PlasticsProductSurchargeContribution[];
    issues: PlasticsProductSurchargeIssue[];
}

export interface PlasticsProductSurchargeResolvers {
    loadBillOfMaterialsLineItems(productVersionId: string): Promise<BillOfMaterialsLineItem[]>;
    loadHardwareItem(hardwareItemId: string): Promise<PlasticsHardwareItem>;
    loadMaterial(materialId: string): Promise<PlasticsMaterial>;
    loadReleasedProductVersion(productId: string): Promise<ProductVersion>;
}

type MutableContribution = PlasticsProductSurchargeContribution;

/**
 * Calculates the current per-part resin surcharge for a plastics product version.
 *
 * Product BOM lines are followed recursively. Material lines are expanded using
 * the material recipe: pound-based hardware ingredients are treated as resin and
 * non-pound ingredients (such as gram-based colorant) are excluded. Hardware is
 * a leaf, and the newest surcharge entry effective on or before `asOf` is used.
 * Vendor/VHD fields remain audit metadata; the latest hardware-item event defines
 * the current surcharge, including a zero-dollar event that ends a surcharge.
 */
export async function calculatePlasticsProductSurcharge(
    rootProductVersionId: string,
    asOf: Date,
    resolvers: PlasticsProductSurchargeResolvers,
): Promise<PlasticsProductSurchargeResult> {
    const issues: PlasticsProductSurchargeIssue[] = [];
    const contributionsByHardwareItemId = new Map<string, MutableContribution>();
    const bomPromises = new Map<string, Promise<BillOfMaterialsLineItem[]>>();
    const hardwarePromises = new Map<string, Promise<PlasticsHardwareItem>>();
    const materialPromises = new Map<string, Promise<PlasticsMaterial>>();
    const productVersionPromises = new Map<string, Promise<ProductVersion>>();
    const asOfTime = asOf.getTime();

    if (!Number.isFinite(asOfTime)) {
        return {
            totalSurchargeDollars: 0,
            complete: false,
            contributions: [],
            issues: [{
                code: 'invalid-surcharge-history',
                entityId: rootProductVersionId,
                message: 'The surcharge calculation date is invalid.',
            }],
        };
    }

    const loadBom = (productVersionId: string) => cached(
        bomPromises,
        productVersionId,
        () => resolvers.loadBillOfMaterialsLineItems(productVersionId),
    );
    const loadHardware = (hardwareItemId: string) => cached(
        hardwarePromises,
        hardwareItemId,
        () => resolvers.loadHardwareItem(hardwareItemId),
    );
    const loadMaterial = (materialId: string) => cached(
        materialPromises,
        materialId,
        () => resolvers.loadMaterial(materialId),
    );
    const loadProductVersion = (productId: string) => cached(
        productVersionPromises,
        productId,
        () => resolvers.loadReleasedProductVersion(productId),
    );

    const addHardwareSurcharge = (
        hardwareItem: PlasticsHardwareItem,
        quantityPounds: number,
    ): void => {
        const currentSurcharge = currentSurchargeCentsPerPound(hardwareItem, asOfTime, issues);
        if (currentSurcharge === null || currentSurcharge === 0) return;

        const surchargeDollars = quantityPounds * currentSurcharge / 100;
        const existing = contributionsByHardwareItemId.get(hardwareItem.id);
        if (existing) {
            existing.quantityPounds += quantityPounds;
            existing.surchargeDollars += surchargeDollars;
            return;
        }

        contributionsByHardwareItemId.set(hardwareItem.id, {
            hardwareItemId: hardwareItem.id,
            partNumber: hardwareItem.partNumber,
            description: hardwareItem.description,
            quantityPounds,
            surchargeCentsPerPound: currentSurcharge,
            surchargeDollars,
        });
    };

    const visitMaterial = async (materialId: string, quantityPounds: number): Promise<void> => {
        let material: PlasticsMaterial;
        try {
            material = await loadMaterial(materialId);
            if (!material?.id) throw new Error('No material was returned.');
        } catch (error) {
            addIssue(issues, {
                code: 'material-load-failed',
                entityId: materialId,
                message: `Unable to load material ${materialId}: ${errorMessage(error)}`,
            });
            return;
        }

        const validIngredients = (material.ingredients ?? []).filter(ingredient => {
            if (Number.isFinite(ingredient.quantity) && ingredient.quantity >= 0) {
                return ingredient.quantity > 0;
            }

            addIssue(issues, {
                code: 'invalid-quantity',
                entityId: ingredient.id,
                message: `Material ingredient ${ingredient.id} has an invalid quantity.`,
            });
            return false;
        });

        const loadedIngredients = await Promise.all(validIngredients.map(async ingredient => {
            try {
                const hardwareItem = await loadHardware(ingredient.hardwareItemId);
                if (!hardwareItem?.id) throw new Error('No hardware item was returned.');
                return { ingredient, hardwareItem };
            } catch (error) {
                addIssue(issues, {
                    code: 'hardware-load-failed',
                    entityId: ingredient.hardwareItemId,
                    message: `Unable to load hardware item ${ingredient.hardwareItemId}: ${errorMessage(error)}`,
                });
                return null;
            }
        }));

        const resinIngredients = loadedIngredients.filter(
            (entry): entry is NonNullable<typeof entry> =>
                entry !== null && entry.hardwareItem.units === HardwareItemUnit.Pounds,
        );
        const totalRecipePounds = resinIngredients.reduce(
            (total, entry) => total + entry.ingredient.quantity,
            0,
        );

        if (totalRecipePounds <= 0) {
            addIssue(issues, {
                code: 'material-recipe-unresolved',
                entityId: material.id,
                message: `${material.identifier} has no pound-based hardware ingredients to use as resin.`,
            });
            return;
        }

        resinIngredients.forEach(({ ingredient, hardwareItem }) => {
            const ingredientQuantityPounds = quantityPounds * ingredient.quantity / totalRecipePounds;
            addHardwareSurcharge(hardwareItem, ingredientQuantityPounds);
        });
    };

    const visitProductVersion = async (
        productVersionId: string,
        productMultiplier: number,
        ancestorProductVersionIds: ReadonlySet<string>,
    ): Promise<void> => {
        if (ancestorProductVersionIds.has(productVersionId)) {
            addIssue(issues, {
                code: 'circular-bom',
                entityId: productVersionId,
                message: `Circular BOM reference encountered at product version ${productVersionId}.`,
            });
            return;
        }

        let lineItems: BillOfMaterialsLineItem[];
        try {
            lineItems = await loadBom(productVersionId);
        } catch (error) {
            addIssue(issues, {
                code: 'bom-load-failed',
                entityId: productVersionId,
                message: `Unable to load BOM for product version ${productVersionId}: ${errorMessage(error)}`,
            });
            return;
        }

        const nextAncestors = new Set(ancestorProductVersionIds);
        nextAncestors.add(productVersionId);

        await Promise.all(lineItems.map(async lineItem => {
            if (!Number.isFinite(lineItem.quantity) || lineItem.quantity < 0) {
                addIssue(issues, {
                    code: 'invalid-quantity',
                    entityId: lineItem.id,
                    message: `BOM line ${lineItem.id} has an invalid quantity.`,
                });
                return;
            }

            const extendedQuantity = productMultiplier * lineItem.quantity;

            if (lineItem.type === BillOfMaterialsLineItemType.Product) {
                try {
                    const childVersion = await loadProductVersion(lineItem.entityId);
                    if (!childVersion?.id) throw new Error('No Released product version was returned.');
                    await visitProductVersion(childVersion.id, extendedQuantity, nextAncestors);
                } catch (error) {
                    addIssue(issues, {
                        code: 'product-version-load-failed',
                        entityId: lineItem.entityId,
                        message: `Unable to load the Released version for product ${lineItem.entityId}: ${errorMessage(error)}`,
                    });
                }
                return;
            }

            if (lineItem.type === BillOfMaterialsLineItemType.Material) {
                await visitMaterial(lineItem.entityId, extendedQuantity);
                return;
            }

            if (lineItem.type !== BillOfMaterialsLineItemType.Hardware) return;

            let hardwareItem: PlasticsHardwareItem;
            try {
                hardwareItem = await loadHardware(lineItem.entityId);
                if (!hardwareItem?.id) throw new Error('No hardware item was returned.');
            } catch (error) {
                addIssue(issues, {
                    code: 'hardware-load-failed',
                    entityId: lineItem.entityId,
                    message: `Unable to load hardware item ${lineItem.entityId}: ${errorMessage(error)}`,
                });
                return;
            }

            // Pieces such as screws and labels are not resin. A non-weight BOM
            // item matters only if someone actually assigned it a surcharge.
            const activeSurcharge = currentSurchargeCentsPerPound(hardwareItem, asOfTime, issues);
            if (activeSurcharge === null || activeSurcharge === 0) return;

            const quantityPounds = toPounds(extendedQuantity, hardwareItem.units);
            if (quantityPounds === null) {
                addIssue(issues, {
                    code: 'unsupported-weight-unit',
                    entityId: hardwareItem.id,
                    message: `${hardwareItem.partNumber} uses ${hardwareItem.units}, which cannot be converted to pounds.`,
                });
                return;
            }
            addHardwareSurcharge(hardwareItem, quantityPounds);
        }));
    };

    await visitProductVersion(rootProductVersionId, 1, new Set());

    const contributions = [...contributionsByHardwareItemId.values()]
        .sort((a, b) => a.partNumber.localeCompare(b.partNumber));

    return {
        totalSurchargeDollars: contributions.reduce((sum, item) => sum + item.surchargeDollars, 0),
        complete: issues.length === 0,
        contributions,
        issues,
    };
}

function currentSurchargeCentsPerPound(
    hardwareItem: PlasticsHardwareItem,
    asOfTime: number,
    issues: PlasticsProductSurchargeIssue[],
): number | null {
    let latest: { effectiveTime: number; cents: number } | null = null;

    for (const entry of hardwareItem.surchargeHistory ?? []) {
        const effectiveTime = entry?.effectiveOn instanceof Date
            ? entry.effectiveOn.getTime()
            : new Date(entry?.effectiveOn as unknown as string).getTime();
        if (!Number.isFinite(effectiveTime)) {
            addIssue(issues, {
                code: 'invalid-surcharge-history',
                entityId: hardwareItem.id,
                message: `${hardwareItem.partNumber} has a surcharge entry with an invalid effective date.`,
            });
            continue;
        }
        if (effectiveTime > asOfTime) continue;

        const cents = Number(entry.surchargeCentsPerPound);
        if (!Number.isFinite(cents) || cents < 0) {
            addIssue(issues, {
                code: 'invalid-surcharge-history',
                entityId: hardwareItem.id,
                message: `${hardwareItem.partNumber} has an invalid surcharge amount.`,
            });
            continue;
        }

        if (!latest || effectiveTime >= latest.effectiveTime) {
            latest = { effectiveTime, cents };
        }
    }

    return latest?.cents ?? 0;
}

function toPounds(quantity: number, units: HardwareItemUnit): number | null {
    if (units === HardwareItemUnit.Pounds) return quantity;
    if (units === HardwareItemUnit.Ounces) return quantity / 16;
    if (units === HardwareItemUnit.Grams) return quantity / 453.59237;
    return null;
}

function cached<T>(cache: Map<string, Promise<T>>, key: string, load: () => Promise<T>): Promise<T> {
    const existing = cache.get(key);
    if (existing) return existing;
    const value = load();
    cache.set(key, value);
    return value;
}

function addIssue(
    issues: PlasticsProductSurchargeIssue[],
    issue: PlasticsProductSurchargeIssue,
): void {
    if (issues.some(existing => existing.code === issue.code && existing.entityId === issue.entityId)) return;
    issues.push(issue);
}

function errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
}
